// VERA-825 / VERA-246: "Checkout summary" card (right column of the checkout).
// Matches Figma: each cart line (title + "N pieces" subtitle + price), a
// divider, then Subtotal / Tax / Total (bold), and a full-width "Pay $X.XX"
// primary button whose label carries the total.
//
// No "Partners discount" line — that was a Figma sample; a discount line only
// renders when real discount data exists (none does today), so it's omitted.

import React from 'react';

import {Box, Divider, Stack, Typography, useTheme} from '@mui/material';
import {Button} from '@repo/ui';

import {toDollar} from 'utils';
import {cartItemTotal, cartSetupFeeTotal, type CartItem} from 'contexts/CartContext';

interface TagShopSummaryCardProps {
  items: CartItem[];
  subtotal: number;
  calculatedTotals: {
    subtotalPrice: number;
    totalTax: number;
    totalShippingPrice: number;
    totalPrice: number;
  } | null;
  /** Total shown on the Pay button. Falls back to subtotal before calculate returns. */
  onPay(): void;
  isPaying: boolean;
  payDisabled: boolean;
}

// VRG-575: was a fourth hardcoded copy of the fee, with a comment saying it
// "mirrors the API's". It read $100 while the API read $100 and the cart read
// $100 — three literals agreeing by luck, one edit away from disagreeing. All
// four now import the single constant from @repo/types.

// "N pieces" subtitle — each cart line is a case of `unitPrice`-priced pieces;
// total pieces = casesUnitsPerCase inferred from casePrice / unitPrice × quantity.
const piecesLabel = (item: CartItem): string => {
  const unitsPerCase =
    item.unitPrice > 0 ? Math.round(item.casePrice / item.unitPrice) : 1;
  const pieces = unitsPerCase * item.quantity;
  return `${pieces.toLocaleString('en-US')} pieces`;
};

export const TagShopSummaryCard = ({
  items,
  subtotal,
  calculatedTotals,
  onPay,
  isPaying,
  payDisabled,
}: TagShopSummaryCardProps) => {
  const theme = useTheme();

  // The calculated subtotal already includes the customization fee (a server-added
  // line item), so show tags-only as "Subtotal" and break the fee out separately.
  //
  // VRG-575: one fee per distinct ARTWORK, not per customised line. This is the
  // same local estimate the cart shows, and it can OVER-state: only the server
  // knows whether this company already paid setup for a design on a previous
  // order. `calculatedTotals` is authoritative for what is actually charged, so
  // when it is present the itemization below is derived from it, not from this.
  // Tags alone, with no setup fee in it. Computed from the lines rather than by
  // subtracting an estimate from the server's subtotal, so the two figures below
  // cannot both be guesses.
  const tagsSubtotal = items.reduce((sum, item) => sum + cartItemTotal(item), 0);

  // What is ACTUALLY charged for setup. When the server has quoted, it is the
  // difference between its subtotal and the tags — which correctly shows $0 for
  // a reorder of already-paid artwork, something the client cannot know on its
  // own. Falls back to the local estimate only before the quote arrives.
  const customizationTotal = calculatedTotals
    ? Math.max(0, calculatedTotals.subtotalPrice - tagsSubtotal)
    : cartSetupFeeTotal(items);

  const displayTotal = calculatedTotals
    ? calculatedTotals.totalPrice
    : tagsSubtotal + customizationTotal;

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary[100]}`,
        borderRadius: 2,
        position: 'sticky',
        top: 24,
      }}
    >
      <Stack spacing={4} p={6}>
        <Typography variant="h6">Checkout summary</Typography>

        {/* Line items */}
        <Stack spacing={3}>
          {items.map((item) => (
            <Stack
              key={item.productId}
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={3}
            >
              <Stack minWidth={0}>
                <Typography variant="body2">{item.title}</Typography>
                <Typography variant="body3" color="text.secondary">
                  {piecesLabel(item)}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{flexShrink: 0}}>
                {toDollar(item.casePrice * item.quantity)}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Divider />

        {/* Totals */}
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Subtotal
            </Typography>
            <Typography variant="body2">{toDollar(tagsSubtotal)}</Typography>
          </Stack>

          {/* VRG-575: shown only when a setup fee is actually being charged. A
              reorder of already-paid artwork shows no row at all, which is the
              honest presentation of "you are not charged for this again". */}
          {customizationTotal > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack sx={{pr: 4}}>
                <Typography variant="body2" color="text.secondary">
                  Customization setup
                </Typography>
                <Typography variant="body4" color="text.secondary" sx={{lineHeight: 1.5}}>
                  Charged once per artwork. Reordering the same design is not charged
                  again.
                </Typography>
              </Stack>
              <Typography variant="body2">{toDollar(customizationTotal)}</Typography>
            </Stack>
          )}

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Tax
            </Typography>
            {/* Not a skeleton — tax isn't "loading", it's just not computable
                until a shipping address is chosen. Show a muted placeholder. */}
            {calculatedTotals ? (
              <Typography variant="body2">
                {toDollar(calculatedTotals.totalTax)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body1">Total</Typography>
            {/* Provisional (subtotal) until tax/shipping calculate — matches the
                Pay button amount, so no skeleton. */}
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {toDollar(displayTotal)}
            </Typography>
          </Stack>
        </Stack>

        <Button
          fullWidth
          variant="primary"
          onClick={onPay}
          loading={isPaying}
          disabled={payDisabled || isPaying}
        >
          {`Pay ${toDollar(displayTotal)}`}
        </Button>
      </Stack>
    </Box>
  );
};
